import { clsx } from 'clsx';
import type { ProviderReferral } from '../../../../../js/api/models/ProviderReferral';
import { useCurrencyFormatter } from '../../../../../js/app/hooks/use-currency-formatter';

export const summaryCardStyles =
  'tw:flex tw:flex-col tw:items-center tw:justify-center tw:text-center tw:rounded-lg tw:border tw:border-zinc-200 tw:bg-white tw:p-4';

interface ReferralsSummaryCardsProps {
  referrals: ProviderReferral[];
  isLoading?: boolean;
}

export function ReferralsSummaryCards({ referrals, isLoading }: ReferralsSummaryCardsProps) {
  const { formatCurrency } = useCurrencyFormatter({ postfix: '', placeholder: '--' });

  const totalReferrals = referrals.length;
  const totalEarned = referrals.reduce((sum, r) => sum + (r.total_earned ?? 0), 0);
  const totalPaid = referrals.reduce((sum, r) => sum + (r.total_paid ?? 0), 0);
  const showBothEarnedAndPaid = totalPaid !== 0 && totalEarned !== totalPaid;

  if (isLoading) {
    return (
      <div className="tw:flex tw:flex-col tw:gap-4 tw:sm:grid tw:sm:grid-cols-3">
        {[1, 2, 3].map((i) => (
          <div
            key={i}
            className="tw:h-24 tw:animate-pulse tw:rounded-lg tw:bg-zinc-100"
            aria-hidden
          />
        ))}
      </div>
    );
  }

  const gridCols = showBothEarnedAndPaid ? 'tw:grid-cols-3' : 'tw:grid-cols-2 tw:sm:grid-cols-3';

  return (
    <div className={clsx('tw:gap-4 tw:grid', gridCols)}>
      <div className={summaryCardStyles}>
        <p className="tw:text-sm tw:font-medium tw:text-zinc-500">Total Referrals</p>
        <p className="tw:mt-1 tw:text-2xl tw:font-bold tw:text-brand-green-0">{totalReferrals}</p>
      </div>
      {showBothEarnedAndPaid ? (
        <>
          <div className={summaryCardStyles}>
            <p className="tw:text-sm tw:font-medium tw:text-zinc-500">Total Earned</p>
            <p className="tw:mt-1 tw:text-2xl tw:font-bold tw:text-brand-green-0">
              {formatCurrency(totalEarned)}
            </p>
          </div>
          <div className={summaryCardStyles}>
            <p className="tw:text-sm tw:font-medium tw:text-zinc-500">Total Paid</p>
            <p className="tw:mt-1 tw:text-2xl tw:font-bold tw:text-brand-green-0">
              {formatCurrency(totalPaid)}
            </p>
          </div>
        </>
      ) : (
        <div className={summaryCardStyles}>
          <p className="tw:text-sm tw:font-medium tw:text-zinc-500">Total Earned</p>
          <p className="tw:mt-1 tw:text-2xl tw:font-bold tw:text-brand-green-0">
            {formatCurrency(totalEarned)}
          </p>
        </div>
      )}
    </div>
  );
}
